import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as configSelectors from '@makemydeal/dr-dash-store/selectors/config';
import * as calculationDetailsSelectors from '@makemydeal/dr-dash-store/selectors/calculationDetailsSelectors';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import { HostEnvironment, hostUrlHelper, locHrefUtil, MANAGER_VIEW_APP_PREFIX } from '@makemydeal/dr-shared-ui-utils';

import { DIGITAL_DEAL_JACKET_PARTNER_ID, DIGITAL_DEAL_JACKET_SOURCE, DDJ_SCRIPT_ID, DDJ_PRELOADER_ID } from '../constants';
import '../custom.d';
import { DDJPreloaderWrapper } from './DDJRouteContainer.style';

export type DDJPreloaderStatus = 'idle' | 'loading' | 'ready' | 'error';

interface DDJPreloaderProps {
    onStatusChange?: (status: DDJPreloaderStatus) => void;
}

const DDJPreloader = ({ onStatusChange }: DDJPreloaderProps) => {
    const [status, setStatus] = useState<DDJPreloaderStatus>('idle');

    const dealxgId = useSelector(offerReduxSelectors.getDealXgId);
    const dealerId = useSelector(dealerSelectors.getDealerId);
    const partnerCode = useSelector(calculationDetailsSelectors.getPartnerCode);
    const partnerDealerId = useSelector(calculationDetailsSelectors.getPartnerDealerId);
    const sourceUrl = useSelector(configSelectors.getDDJSourceUrl);
    const environment = hostUrlHelper.getHostUrlEnvironment(MANAGER_VIEW_APP_PREFIX, locHrefUtil.getLocHref());
    const redirectBaseUrl = `https://${window.location.hostname}`;

    const ddjEnv = (() => {
        const environments: Record<HostEnvironment, string> = {
            [HostEnvironment.Dev]: 'dev',
            [HostEnvironment.Dev2]: 'dev',
            [HostEnvironment.Local]: 'dev',
            [HostEnvironment.QA]: 'uat',
            [HostEnvironment.DarkProd]: 'prod',
            [HostEnvironment.UAT]: 'uat',
            [HostEnvironment.Prod]: 'prod',
            [HostEnvironment.None]: 'uat', // pr environment falls into this category
            [HostEnvironment.Unknown]: 'uat' // https://coxauto.slack.com/archives/C02BN0CEJ93/p1770096706845369?thread_ts=1770061353.813329&cid=C02BN0CEJ93
        };
        return environments[environment];
    })();

    const updateStatus = useCallback(
        (newStatus: DDJPreloaderStatus) => {
            setStatus(newStatus);
            onStatusChange?.(newStatus);
        },
        [onStatusChange]
    );

    useEffect(() => {
        const logPreloaderStatus = (statusValue: DDJPreloaderStatus, source: string) => {
            logNewRelicPageAction('MV:DDJ - Preloader Status', {
                dealerId: String(dealerId),
                dealXgId: dealxgId,
                status: statusValue,
                source
            });
        };

        // Check if the custom element 'ddj-app' has already been defined
        if (customElements.get('ddj-app')) {
            updateStatus('ready');
            logPreloaderStatus('ready', 'already_defined');
            return;
        }

        // Check if script is already being loaded
        if (document.getElementById(DDJ_SCRIPT_ID)) {
            updateStatus('loading');
            logPreloaderStatus('loading', 'script_in_progress');
            return;
        }

        updateStatus('loading');
        logPreloaderStatus('loading', 'script_injected');

        const scriptLoadStart = performance.now();

        const script = document.createElement('script');
        script.src = sourceUrl;
        script.type = 'text/javascript';
        script.id = DDJ_SCRIPT_ID;

        script.onload = () => {
            const durationMs = Math.round(performance.now() - scriptLoadStart);
            updateStatus('ready');
            logNewRelicPageAction('MV:DDJ - Script Load Duration', {
                dealerId: String(dealerId),
                dealXgId: dealxgId,
                durationMs
            });
            logPreloaderStatus('ready', 'script_loaded');
        };

        script.onerror = () => {
            updateStatus('error');
            logPreloaderStatus('error', 'script_load_failure');
            logNewRelicPageAction('MV:DDJ - Script Load Failure', {
                dealerId: String(dealerId),
                dealXgId: dealxgId,
                message: 'Failure while loading Digital Deal Jacket script'
            });
        };

        document.body.appendChild(script);

        return () => {
            const ddjScript = document.getElementById(DDJ_SCRIPT_ID) as HTMLScriptElement | null;
            if (ddjScript) {
                ddjScript.onload = null;
                ddjScript.onerror = null;
                ddjScript.remove();
            }
        };
    }, [sourceUrl, updateStatus]);

    return (
        <DDJPreloaderWrapper id={DDJ_PRELOADER_ID} data-testid="ddj-preloader" data-status={status}>
            {status !== 'error' && (
                <ddj-app
                    data-testid="ddj-preloaded-pbc"
                    external-id={dealxgId}
                    source={DIGITAL_DEAL_JACKET_SOURCE}
                    env={ddjEnv}
                    redirect-url={redirectBaseUrl}
                    partner-id={partnerCode || DIGITAL_DEAL_JACKET_PARTNER_ID}
                    partner-dealer-id={partnerDealerId || dealerId}
                ></ddj-app>
            )}
        </DDJPreloaderWrapper>
    );
};

export default DDJPreloader;
